import React from "react";
import {useTranslation} from "react-i18next";

import type IsaacTableColumn from "../types/IsaacTableColumn";
import type CalculationNoteFoundationLoadCase from "../../../types/CalculationNoteFoundationLoadCase";
import styles from "../components/cells/Cell.module.scss";
import headerStyles from "../components/calculationNoteFoundationLoadCases/CalculationNoteFoundationLoadCases.module.scss";
import Cell from "../components/cells/Cell";
import CellBoolean from "../components/cells/CellBoolean";

const useFoundationResultsTableColumns = (): {
  primaryHeaders: IsaacTableColumn[];
  secondaryHeaders: IsaacTableColumn[];
} => {
  const {t} = useTranslation(["constructionCalculations"]);

  const primaryHeaders = [
    {key: "uls", name: t("uls"), minWidth: 100, colSpan: 5},
    {key: "sls", name: t("sls"), minWidth: 100, colSpan: 6},
  ];

  const secondaryHeaders = [
    {
      key: "code",
      name: t("loadCase"),
      minWidth: 200,
      maxWidth: 200,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell value={item ? t(item.code) : ""} className={styles.cellTitle} />
      ),
    },
    {
      key: "ulsBearingCapacity",
      name: t("bearingCapacity"),
      minWidth: 50,
      maxWidth: 120,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <CellBoolean value={item?.ulsBearingCapacity} />
      ),
    },
    {
      key: "ulsSlidingEquilibrium",
      name: t("slidingEquilibrium"),
      minWidth: 110,
      maxWidth: 150,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell
          value={item?.ulsSlidingEquilibrium}
          className={styles.cellNumber}
        />
      ),
    },
    {
      key: "ulsTiltBalance",
      name: t("tiltBalance"),
      minWidth: 110,
      maxWidth: 150,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell value={item?.ulsTiltBalance} className={styles.cellNumber} />
      ),
    },
    {
      key: "ulsUsedCalculationMethod",
      name: t("usedCalculationMethod"),
      minWidth: 105,
      maxWidth: 200,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell
          value={item?.ulsUsedCalculationMethod}
          className={styles.cellString}
        />
      ),
    },
    {
      key: "slsBearingCapacity",
      name: t("bearingCapacity"),
      minWidth: 100,
      maxWidth: 150,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell value={item?.slsBearingCapacity} className={styles.cellNumber} />
      ),
    },
    {
      key: "slsSlidingEquilibrium",
      name: t("slidingEquilibrium"),
      minWidth: 100,
      maxWidth: 150,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell
          value={item?.slsSlidingEquilibrium}
          className={styles.cellNumber}
        />
      ),
    },
    {
      key: "slsTiltBalance",
      name: t("tiltBalance"),
      minWidth: 100,
      maxWidth: 150,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell value={item?.slsTiltBalance} className={styles.cellNumber} />
      ),
    },
    {
      key: "slsMobilisation",
      name: t("mobilisation"),
      minWidth: 50,
      maxWidth: 150,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell value={item?.slsMobilisation} className={styles.cellNumber} />
      ),
    },
    {
      key: "slsInclination",
      name: t("inclination"),
      minWidth: 110,
      maxWidth: 150,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell value={item?.slsInclination} className={styles.cellNumber} />
      ),
    },
    {
      key: "slsDisplacementAtTop",
      name: t("displacementAtTop"),
      minWidth: 110,
      maxWidth: 150,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <Cell
          value={item?.slsDisplacementAtTop}
          className={styles.cellNumber}
        />
      ),
    },
    {
      key: "isValid",
      name: t("foundationOk"),
      minWidth: 50,
      maxWidth: 120,
      headerClassName: headerStyles.multiLineHeader,
      onRender: (item?: CalculationNoteFoundationLoadCase) => (
        <CellBoolean value={item?.isValid} />
      ),
    },
  ];

  return {primaryHeaders, secondaryHeaders};
};

export default useFoundationResultsTableColumns;
